import {
  LegalLayout,
  Section,
  SubList,
} from "../components/legal/LegalLayout";

export default function Terms() {
  return (
    <LegalLayout
      title="Terms & Conditions"
      updated="August 11, 2026"
      pageTestId="page-terms"
    >
      <Section title="1. Acceptance of terms">
        <p>
          These Terms &amp; Conditions (“Terms”) govern your access to and use
          of the Ivory Pulse website, waitlist, demonstrations, and, once
          enabled, the Pulse One pre-order flow and wearable product. The
          information presented on the site is provided for presentation,
          waitlist registration and product-interest purposes. By joining
          the waitlist, reserving a unit, or using any Pulse experience, you
          agree to these Terms.
        </p>
      </Section>

      <Section title="2. Company information">
        <SubList
          items={[
            "Legal name: IVORY PULSE SRL",
            "Registration (CUI): 52619680",
            "Registered office: Romania",
            "Contact email: contact@ivory-pulse.com",
          ]}
        />
      </Section>

      <Section title="3. Product description">
        <p>
          Pulse One is a consumer wearable <strong>AI companion</strong>{" "}
          bracelet. It is designed to stay close, understand context, and
          connect you to a cloud-based AI companion experience directly from
          your wrist. Pulse One is designed around standalone connectivity
          (built-in cellular via eSIM and Wi-Fi); an optional mobile app is
          offered for onboarding, account management, settings, firmware
          updates and optional integrations. Pulse One is{" "}
          <strong>
            not a medical device, not a fitness tracker, not a heart-rate or
            pulse monitor, not a sleep tracker and not a healthcare product
          </strong>
          . It is not intended to diagnose, treat, cure or prevent any medical
          condition.
        </p>
      </Section>

      <Section title="4. Founders Edition and pre-sale">
        <p>
          The Founders Edition is limited to 1,000 units and is offered at a
          founder price of <strong>$300</strong> before moving to a regular
          price of <strong>$330</strong>. Pre-sale: <strong>Coming soon</strong>.
          The opening date will be announced. Reservations and pre-orders are subject
          to availability, confirmation, and these Terms.
        </p>
        <p>
          Joining the waitlist is free and does <strong>not</strong> create a
          binding order, payment obligation or guaranteed allocation.
          Specifically, joining the waitlist does not guarantee unit
          availability, a delivery date, a final price, a specific finish, or
          purchase allocation. Founders Edition allocation is subject to the
          checkout terms presented at the time payments open.
        </p>
      </Section>

      <Section title="5. Price, taxes and currency">
        <p>
          Prices are shown in U.S. dollars unless indicated otherwise. Prices
          may exclude applicable sales taxes, VAT, GST, import duties or other
          statutory charges, which will be calculated where required at
          checkout or at customs on arrival, depending on your destination.
        </p>
      </Section>

      <Section title="6. Payment (once pre-sale opens)">
        <p>
          When payments are enabled, charges will be processed by our payment
          provider. Payment details are collected and handled by the provider
          in accordance with its own terms and security standards. We do not
          store full card details on our servers.
        </p>
        <p>
          Until payments are enabled, the site is waitlist-only and no charges
          are collected.
        </p>
      </Section>

      <Section title="7. Shipping, delivery and delays">
        <p>
          Estimated shipping is <strong>late summer / autumn 2026</strong>.
          Shipping dates are estimates, not guarantees. If we anticipate a
          significant delay, we will notify pre-order customers by email. See
          our Shipping &amp; Pre-Order Policy for more detail.
        </p>
      </Section>

      <Section title="8. Prototype disclaimer">
        <p>
          Final product imagery is intentionally withheld until the design
          reveal. Final product specifications, finishes, packaging,
          materials, firmware, and companion experience may evolve before
          shipment. Where material changes occur, affected customers will be
          informed before fulfilment.
        </p>
      </Section>

      <Section title="9. Colour / finish selection">
        <p>
          Pulse One is offered in three finishes:{" "}
          <strong>Obsidian Black</strong>, <strong>Champagne Gold</strong> and{" "}
          <strong>Chrome Silver</strong>. You may select your preferred finish
          when joining the waitlist or, later, at checkout. Availability of any
          individual finish may change; we will work to honour your preference
          whenever possible.
        </p>
      </Section>

      <Section title="10. AI companion service">
        <p>
          The Pulse One companion experience depends on an active network
          connection (through built-in cellular via eSIM or Wi-Fi) and
          cloud-based AI services operated by us and by trusted third-party
          providers. An optional companion app is offered for onboarding,
          account management, settings, firmware updates and optional
          integrations. Service features, quality, latency and availability
          may depend on factors outside our control, including network
          conditions, device compatibility and upstream provider availability.
        </p>
      </Section>

      <Section title="11. “Lifetime core AI access”">
        <p>
          Founders Edition purchasers receive{" "}
          <strong>
            lifetime access to the core Pulse AI companion experience
          </strong>
          . This means the core companion features as defined by us at the
          time of launch and as reasonably maintained over the product’s
          supported lifetime.
        </p>
        <p>
          Lifetime access does{" "}
          <strong>not</strong> mean unlimited AI usage forever, and does not
          cover future advanced or premium tiers, specialised models,
          additional integrations, third-party service costs, or features
          introduced after launch that we designate as optional paid add-ons.
          Fair-use limits, abuse prevention, reasonable rate limits and
          service-availability safeguards may apply.
        </p>
      </Section>

      <Section title="12. Fair use and service availability">
        <p>
          To protect the quality of the service for everyone, we may apply
          reasonable fair-use thresholds, rate limits, content-safety
          guardrails and abuse-prevention measures. We may suspend or restrict
          accounts engaging in fraudulent, abusive, illegal or service-damaging
          behaviour.
        </p>
      </Section>

      <Section title="13. User responsibilities">
        <SubList
          items={[
            "Use Pulse One lawfully and in line with these Terms.",
            "Provide accurate contact, shipping and payment information.",
            "Keep your Ivory Pulse account credentials confidential.",
            "Do not attempt to reverse-engineer, misuse, or interfere with the service, hardware or AI systems.",
            "Do not use Pulse One to generate, distribute or solicit unlawful, harmful or harassing content.",
          ]}
        />
      </Section>

      <Section title="14. No medical, health or fitness use">
        <p>
          Pulse One must not be used as a medical device, diagnostic tool,
          safety device, heart-rate monitor, pulse monitor, sleep tracker,
          fitness tracker or emergency communication device. If you have a
          medical concern, please contact a qualified professional — Pulse
          One is not a substitute for medical advice, diagnosis, treatment
          or emergency services.
        </p>
      </Section>

      <Section title="15. Warranty and product support">
        <p>
          We intend to provide a limited manufacturer warranty covering
          material and workmanship defects under normal consumer use, with
          duration and scope confirmed before payments open. Statutory consumer
          rights in your jurisdiction apply in addition to any voluntary
          warranty.
        </p>
      </Section>

      <Section title="15b. Intellectual property">
        <p>
          All trade marks, brand names, product names, logos, marketing copy,
          imagery, photography, illustrations, software, firmware, designs
          and content presented on this site are the property of Ivory Pulse
          (IVORY PULSE SRL) and/or its licensors and partners, and are
          protected by intellectual property laws. You may not copy,
          reproduce, distribute, modify, reverse-engineer, scrape or create
          derivative works from any of this material without our prior
          written permission, except where permitted by mandatory law.
        </p>
      </Section>

      <Section title="16. Limitation of liability">
        <p>
          To the maximum extent permitted by applicable law, Ivory Pulse SRL,
          its officers, employees and agents shall not be liable for indirect,
          incidental, special, punitive or consequential damages, or loss of
          profits, data, goodwill or business opportunity arising out of or
          relating to the product or service. Nothing in these Terms limits
          liability that cannot be limited or excluded under applicable
          mandatory law (including statutory consumer rights).
        </p>
      </Section>

      <Section title="17. Changes to these Terms">
        <p>
          We may update these Terms from time to time. Material changes will be
          announced by email or on the website before they take effect. Your
          continued use of the service after the effective date constitutes
          acceptance of the updated Terms.
        </p>
      </Section>

      <Section title="18. Governing law">
        <p>
          These Terms are governed by the laws of Romania, the country of the
          registered seat of IVORY PULSE SRL. Mandatory consumer protection
          rules of your country of residence remain unaffected.
        </p>
      </Section>

      <Section title="19. Contact">
        <p>
          For questions about these Terms, please email{" "}
          <a
            className="underline decoration-white/30 hover:decoration-white"
            href="mailto:contact@ivory-pulse.com"
          >
            contact@ivory-pulse.com
          </a>
          . For account or product help, write to{" "}
          <a
            className="underline decoration-white/30 hover:decoration-white"
            href="mailto:support@ivory-pulse.com"
          >
            support@ivory-pulse.com
          </a>
          . For Founders Edition reservations, write to{" "}
          <a
            className="underline decoration-white/30 hover:decoration-white"
            href="mailto:sales@ivory-pulse.com"
          >
            sales@ivory-pulse.com
          </a>
          .
        </p>
      </Section>
    </LegalLayout>
  );
}
